import React, { ReactElement } from 'react'
import { Field, FieldArray, useFormikContext } from 'formik'
import { Button, Element, Icon, Table } from 'react-bulma-components'
import { AddResourceIcon, RemoveIcon } from '../../../icons'
import { ContractualTimeSlot } from '../../../../api/users'
import { formatHoursAsTime } from '../../../../utils/date'
import { calculateWeekTotals } from './contractual-schedules-utils'

import { createNewTimeSlot } from './contractual-schedules-utils'
import {
  Clipboard as ClipboardType,
  ClipboardProvider,
  useClipboardContext,
} from '../../../../hooks/use-clipboard'
import { Day } from '../../../schedule/day'
import { DayCellActions } from '../../../schedule/day-cell-actions'
import { TimePickerField } from '../../../form/fields/time-picker'

interface ContractualWeekViewProps {
  basePath: string // Full path to the schedule (e.g., "newUsers.0.contractualSchedules")
  scheduleIndex: number
}

/**
 * Helper function to get nested value from touched object
 * Formik stores touched values in nested structure
 */
const getNestedTouched = (path: string, touchedObj: any): boolean => {
  const parts = path.split('.')
  let current = touchedObj
  for (const part of parts) {
    if (current === undefined || current === null) return false
    current = current[part]
  }
  return current === true
}

/**
 * Helper function to check if an error should be displayed
 * Only show error if field is touched or form has been submitted
 */
const shouldShowError = (
  fieldName: string,
  touched: boolean,
  submitCount: number,
  errors: any,
): string | undefined => {
  const fieldTouched = touched || getNestedTouched(fieldName, touched)
  const formSubmitted = submitCount > 0
  if (fieldTouched || formSubmitted) {
    return errors[fieldName] as string | undefined
  }
  return undefined
}

/**
 * Component for a day column in the week view
 */
const DayColumn: React.FC<{
  basePath: string
  scheduleIndex: number
  dayName: string
  dayLabel: string
}> = ({ basePath, scheduleIndex, dayName, dayLabel }) => {
  const baseName = `${basePath}.${scheduleIndex}.schedule.${dayName}`
  const { setFieldValue } = useFormikContext<any>()

  return (
    <Clipboard<ContractualTimeSlot[]>>
      {clipboard => {
        return (
          <FieldArray name={`${baseName}.timeSlots`}>
            {({ remove, push, form }) => {
              // Parse the basePath to get the values from form.values
              const pathParts = basePath.split('.')
              let currentValue: any = form.values
              for (const part of pathParts) {
                if (part.match(/^\d+$/)) {
                  currentValue = currentValue?.[parseInt(part)]
                } else {
                  currentValue = currentValue?.[part]
                }
              }
              const currentTimeSlots: ContractualTimeSlot[] =
                currentValue?.[scheduleIndex]?.schedule?.[dayName]?.timeSlots || []
              const hasTimeSlots = currentTimeSlots.length > 0

              const addTimeSlot = () => {
                push(createNewTimeSlot())
              }

              return (
                <Day
                  label={dayLabel}
                  right={
                    hasTimeSlots ? (
                      <DayCellActions
                        onCopy={() => {
                          if (currentTimeSlots.length > 0) {
                            clipboard.copy(currentTimeSlots)
                          }
                        }}
                        onPaste={() => {
                          if (!clipboard.value || !isClipboardDay(clipboard.value)) return
                          setFieldValue(`${baseName}.timeSlots`, clipboard.value)
                        }}
                        copyDisabled={clipboard.isEqualTo(currentTimeSlots)}
                        pasteDisabled={
                          clipboard.isEmpty ||
                          clipboard.isEqualTo(currentTimeSlots) ||
                          !isClipboardDay(clipboard.value)
                        }
                        dataTestPrefix={dayName}
                      />
                    ) : undefined
                  }
                  tdStyle={{ verticalAlign: 'top', padding: '0.5rem', width: '150px' }}
                >
                  {currentTimeSlots.length === 0 ? (
                    <Button
                      size="small"
                      onClick={addTimeSlot}
                      color="primary"
                      outlined
                      type="button"
                    >
                      <Icon>
                        <AddResourceIcon />
                      </Icon>
                      <Element renderAs="span">Créneau</Element>
                    </Button>
                  ) : (
                    <Element display="flex" flexDirection="column" style={{ gap: '0.5rem' }}>
                      {currentTimeSlots.map((timeSlot, index) => {
                        const slotBaseName = `${basePath}.${scheduleIndex}.schedule.${dayName}.timeSlots.${index}`
                        return (
                          <Element
                            key={index}
                            mb={2}
                            style={{
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              padding: '0.75rem',
                            }}
                          >
                            <Element style={{ flex: '1 1 120px', minWidth: '100px' }} mb={2}>
                              <Field
                                name={`${slotBaseName}.startHour`}
                                component={TimePickerField}
                              />
                            </Element>
                            <Element style={{ flex: '1 1 120px', minWidth: '100px' }}>
                              <Field name={`${slotBaseName}.endHour`} component={TimePickerField} />
                            </Element>
                            <Element display="flex" justifyContent="flex-end" mt={1}>
                              <Button
                                color="primary"
                                outlined
                                size="small"
                                onClick={() => remove(index)}
                                type="button"
                              >
                                <Icon size="small">
                                  <RemoveIcon />
                                </Icon>
                              </Button>
                            </Element>
                          </Element>
                        )
                      })}
                      <Button
                        size="small"
                        onClick={addTimeSlot}
                        color="primary"
                        outlined
                        type="button"
                        style={{ marginTop: '0.5rem' }}
                      >
                        <Icon>
                          <AddResourceIcon />
                        </Icon>
                        <Element renderAs="span">Créneau</Element>
                      </Button>
                    </Element>
                  )}
                </Day>
              )
            }}
          </FieldArray>
        )
      }}
    </Clipboard>
  )
}

/**
 * Week view component for contractual schedules (similar to work-periods-editor)
 */
export const ContractualWeekView: React.FC<ContractualWeekViewProps> = ({
  basePath,
  scheduleIndex,
}) => {
  const { values, errors, submitCount } = useFormikContext<any>()
  const daysOfWeek = [
    { name: 'monday', label: 'Lun' },
    { name: 'tuesday', label: 'Mar' },
    { name: 'wednesday', label: 'Mer' },
    { name: 'thursday', label: 'Jeu' },
    { name: 'friday', label: 'Ven' },
    { name: 'saturday', label: 'Sam' },
    { name: 'sunday', label: 'Dim' },
  ] as const

  // Calculate totals for all days
  const pathParts = basePath.split('.')
  let currentValue: any = values
  for (const part of pathParts) {
    if (part.match(/^\d+$/)) {
      currentValue = currentValue?.[parseInt(part)]
    } else {
      currentValue = currentValue?.[part]
    }
  }
  const schedule = currentValue?.[scheduleIndex]?.schedule

  const weekTotal = schedule ? calculateWeekTotals(schedule) : 0

  // Get error for total mismatch to display in total row
  const totalMismatchErrorPath = `${basePath}.${scheduleIndex}.schedule.totalMismatch`

  const totalMismatchError = shouldShowError(totalMismatchErrorPath, true, submitCount, errors)

  return (
    <ClipboardProvider<ContractualTimeSlot[] | ContractualWeekSchedule>>
      <Table bordered size="fullwidth">
        <tbody>
          <tr>
            {daysOfWeek.map(day => (
              <DayColumn
                key={day.name}
                basePath={basePath}
                scheduleIndex={scheduleIndex}
                dayName={day.name}
                dayLabel={day.label}
              />
            ))}
          </tr>
          {/* Global total row */}
          <tr>
            <Element
              renderAs="td"
              colSpan={daysOfWeek.length}
              textWeight="bold"
              style={{ padding: '0.5rem', backgroundColor: '#e8e8e8', textAlign: 'center' }}
            >
              <Element>
                <strong>Total Semaine:</strong> {formatHoursAsTime(weekTotal)}
                {totalMismatchError && (
                  <Element
                    renderAs="div"
                    textColor="danger"
                    textSize={7}
                    mt={1}
                    style={{ fontWeight: 'normal' }}
                  >
                    {totalMismatchError}
                  </Element>
                )}
              </Element>
            </Element>
          </tr>
        </tbody>
      </Table>
    </ClipboardProvider>
  )
}

// Helper component to access clipboard context
const Clipboard = <T,>({
  children,
}: {
  children: (clipboard: ClipboardType<T>) => ReactElement
}): ReactElement => {
  const clipboard = useClipboardContext<T>()
  return children(clipboard)
}

// Type for a full week schedule (for clipboard)
type ContractualWeekSchedule = {
  monday: ContractualTimeSlot[]
  tuesday: ContractualTimeSlot[]
  wednesday: ContractualTimeSlot[]
  thursday: ContractualTimeSlot[]
  friday: ContractualTimeSlot[]
  saturday: ContractualTimeSlot[]
  sunday: ContractualTimeSlot[]
}

// Helper to check if clipboard value is a day (array) or week (object)
const isClipboardDay = (
  clipboardValue?: ContractualTimeSlot[] | ContractualWeekSchedule,
): clipboardValue is ContractualTimeSlot[] => {
  if (!clipboardValue) return false
  return Array.isArray(clipboardValue)
}
